/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { User, Mail, Phone } from "lucide-react";
import { useBooking } from "../../context/BookingContext";
import Button from "../common/Button";
import Input from "../common/Input";
import AddressSearch from "../common/AddressSearch";
import { contactFormSchema, createContactFormSchema } from "../../utils/validation";
import { updateSessionData, getSessionData, clearStaleBookingState } from "../../utils/cookies";
import { analytics } from "../../utils/analytics";
import { databaseService } from "../../services/databaseService";
import { userJourneyService } from "../../services/userJourneyService";
import { formWebhookService } from "../../services/formWebhookService";
import { settingsService } from "../../services/settingsService";
import { campaignTokenService } from "../../services/campaignTokenService";
import { useUTMTracking } from "../../hooks/useUTMTracking";
import { Customer } from "../../types/user";
import { PerformanceProfiler } from "../../utils/performanceProfiler";
import PodiumWelcomeModal from "../modals/PodiumWelcomeModal";

// Utility function to parse Australian address string into suburb
// WARNING: This is a fallback - Google Places structured data should be used when available!
const parseSuburbFromAddress = (addressString: string): string => {
  if (!addressString || typeof addressString !== 'string') {
    return '';
  }

  const parts = addressString.split(',').map(part => part.trim());
  
  // Australian states for identification
  const australianStates = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT', 'VICTORIA', 'NEW SOUTH WALES', 'QUEENSLAND', 'SOUTH AUSTRALIA', 'WESTERN AUSTRALIA', 'TASMANIA', 'NORTHERN TERRITORY', 'AUSTRALIAN CAPITAL TERRITORY'];
  
  // Find state first (more reliable identifier)
  let stateIndex = -1;
  for (let i = 0; i < parts.length; i++) {
    if (australianStates.includes(parts[i].toUpperCase())) {
      stateIndex = i;
      break;
    }
  }
  
  // If we found a state, suburb should be the part before it
  if (stateIndex > 0) {
    return parts[stateIndex - 1] || '';
  }
  
  // Fallback: try postcode identification
  for (let i = 0; i < parts.length; i++) {
    if (/^\d{4}$/.test(parts[i]) && i >= 2) {
      return parts[i - 2] || '';
    }
  }
  
  // Last resort: basic parsing (legacy behavior)
  console.warn('⚠️  Using basic address parsing fallback - Google Places recommended');
  return parts.length >= 2 ? parts[1] || '' : '';
};

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  referralSource?: string;
}

const ContactForm: React.FC = () => {
  PerformanceProfiler.mark("contact-form-start");

  const navigate = useNavigate();
  const { setCustomer, setCurrentStep } = useBooking();
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [validationSchema, setValidationSchema] = useState(contactFormSchema);
  const [isAddressValidated, setIsAddressValidated] = useState(false); // Track address validation state
  const [sessionLoaded, setSessionLoaded] = useState(false); // Track if session data has been loaded

  // Podium welcome modal — show when ?pm is in the URL
  const [showPodiumModal, setShowPodiumModal] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('pm');
  });

  PerformanceProfiler.mark("utm-tracking-start");
  const { utmData } = useUTMTracking();
  PerformanceProfiler.measure("utm-tracking-init", "utm-tracking-start");

  // Recovery flow — detect ?recover= token and hydrate session from backend
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const recoverToken = urlParams.get('recover');
    if (!recoverToken) return;

    const recoverSession = async () => {
      setRecovering(true);
      try {
        const response = await fetch(`/api/recovery/${encodeURIComponent(recoverToken)}`);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.warn('⚠️ Recovery token invalid:', errorData.error || response.status);
          setRecovering(false);
          return; // Fall through to normal contact form
        }

        const data = await response.json();
        if (!data.success || !data.customer) {
          console.warn('⚠️ Recovery response missing customer data');
          setRecovering(false);
          return;
        }

        const { customer, lead } = data;

        // Hydrate BookingContext
        setCustomer({
          id: customer.id,
          firstName: customer.firstName,
          lastName: customer.lastName,
          email: customer.email,
          phone: customer.phone,
          address: customer.address,
          suburb: customer.suburb || '',
        });

        // Hydrate localStorage session
        updateSessionData({
          customerData: {
            id: customer.id,
            firstName: customer.firstName,
            lastName: customer.lastName,
            email: customer.email,
            phone: customer.phone,
            address: customer.address,
            suburb: customer.suburb || '',
          },
          formData: {
            firstName: customer.firstName,
            lastName: customer.lastName,
            email: customer.email,
            phone: customer.phone,
            address: customer.address,
          },
          leadId: lead?.id?.toString(),
          currentStep: 'details',
        });

        // Track recovery event
        analytics.trackFormStart('recovery_session');
        userJourneyService.recordStep('session_recovered', {
          recoveryToken: recoverToken.substring(0, 8) + '...',
          customerId: customer.id,
          leadId: lead?.id,
          timestamp: new Date().toISOString(),
        });

        console.log(`✅ Session recovered for ${customer.firstName} ${customer.lastName}`);

        // Clean URL and navigate to details
        window.history.replaceState({}, '', window.location.pathname);
        setCurrentStep('measurement');
        navigate('/details');
      } catch (error) {
        console.error('Recovery failed:', error);
        setRecovering(false);
      }
    };

    recoverSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load dynamic validation schema
  useEffect(() => {
    const loadValidationSchema = async () => {
      try {
        const dynamicSchema = await createContactFormSchema();
        setValidationSchema(dynamicSchema);
      } catch (error) {
        console.warn('Failed to load dynamic validation schema, using fallback:', error);
        // Keep using the default contactFormSchema
      }
    };

    loadValidationSchema();
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    getValues,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: {},
  });

  // Compute address validation status directly from session data
  const getAddressValidationFromSession = () => {
    const sessionData = getSessionData();
    const addressValue = watch("address");
    
    if (sessionData?.addressValidation && addressValue) {
      return sessionData.addressValidation.isValidated;
    }
    return false;
  };

  // Use computed validation status instead of state
  const computedIsAddressValidated = sessionLoaded ? getAddressValidationFromSession() : false;



  // Referral field settings
  const [referralFieldEnabled, setReferralFieldEnabled] = useState(true);
  const [referralFieldLabel, setReferralFieldLabel] = useState(
    "How did you hear about us?"
  );
  const [referralFieldPlaceholder, setReferralFieldPlaceholder] = useState(
    "Enter referral source (e.g., Google, Facebook, friend referral)"
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [referralUrlParamName, setReferralUrlParamName] = useState("code");

  // Load saved data from session
  useEffect(() => {
    PerformanceProfiler.mark("form-session-load-start");

    const sessionData = getSessionData();

    // Load form data (separate from customer data)
    if (sessionData?.formData) {
      const { firstName, lastName, email, phone, address, referralSource } =
        sessionData.formData;
      setValue("firstName", firstName || "");
      setValue("lastName", lastName || "");
      setValue("email", email || "");
      setValue("phone", phone || "");
      setValue("address", address || "");
      setValue("referralSource", referralSource || "");

      // Restore address validation state from session
      if (sessionData.addressValidation && address) {
        const isValidated = sessionData.addressValidation.isValidated;
        setIsAddressValidated(isValidated);
        console.log(
          "🔄 Restored address validation state:",
          sessionData.addressValidation
        );
        
        // Also trigger the onChange to ensure form state is updated
        if (isValidated) {
          // Force update the form validation state by re-triggering the address onChange
          setTimeout(() => {
            const addressValue = watch("address");
            if (addressValue) {
              // This will ensure the form knows the address is validated
              setIsAddressValidated(true);
            }
          }, 100);
        }
      }

      // Log for debugging in development
      if (process.env.NODE_ENV === "development") {
        console.log(
          "🔄 Restored form data from session:",
          sessionData.formData
        );
      }
    } else if (sessionData?.customerData) {
      // Fallback to customer data if form data doesn't exist
      const { firstName, lastName, email, phone, address, referralSource } =
        sessionData.customerData;
      setValue("firstName", firstName || "");
      setValue("lastName", lastName || "");
      setValue("email", email || "");
      setValue("phone", phone || "");
      setValue("address", address || "");
      setValue("referralSource", referralSource || "");

      // Restore address validation state from session
      if (sessionData.addressValidation && address) {
        const isValidated = sessionData.addressValidation.isValidated;
        setIsAddressValidated(isValidated);
        console.log(
          "🔄 Restored address validation state from customerData:",
          sessionData.addressValidation
        );
        
        // Also ensure the form validation state is updated
        if (isValidated) {
          setTimeout(() => {
            const addressValue = watch("address");
            if (addressValue) {
              setIsAddressValidated(true);
            }
          }, 100);
        }
      }
    }

    // Mark session as loaded
    setSessionLoaded(true);

    analytics.trackPageView("contact_form");

    // Record user journey step
    userJourneyService.recordStep("contact_form_viewed", {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      referrer: document.referrer || "direct",
    });

    PerformanceProfiler.measure("form-session-load", "form-session-load-start");
    PerformanceProfiler.measure(
      "total-contact-form-init",
      "contact-form-start"
    );
  }, [setValue]);

  // Campaign-token prefill (name/email/phone) arrives via an async network call that
  // resolves AFTER the sync session-load effect above already ran, so it's otherwise
  // missed. Await validation completion, then fill in any fields the customer hasn't
  // already typed into -- never overwrites in-progress user input.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get("t")) return;

    campaignTokenService.ready.then(() => {
      const sessionData = getSessionData();
      const prefill = sessionData?.formData;
      if (!prefill) return;

      const current = getValues();
      if (!current.firstName && prefill.firstName) setValue("firstName", prefill.firstName);
      if (!current.lastName && prefill.lastName) setValue("lastName", prefill.lastName);
      if (!current.email && prefill.email) setValue("email", prefill.email);
      if (!current.phone && prefill.phone) setValue("phone", prefill.phone);
    });
  }, [setValue, getValues]);

  // Reset address validation when address is cleared
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const addressValue = watch("address");
    if (!addressValue) {
      setIsAddressValidated(false);
    }
  }, [watch]);

  // Load referral settings and check URL parameters
  useEffect(() => {
    const loadReferralSettings = async () => {
      try {
        // Load referral field settings from database
        const [enabled, label, placeholder, paramName] = await Promise.all([
          settingsService.getSetting("referral_field_enabled"),
          settingsService.getSetting("referral_field_label"),
          settingsService.getSetting("referral_field_placeholder"),
          settingsService.getSetting("referral_url_param_name"),
        ]);

        setReferralFieldEnabled(enabled !== null ? enabled : true);
        setReferralFieldLabel(
          label !== null ? label : "How did you hear about us?"
        );
        setReferralFieldPlaceholder(
          placeholder !== null
            ? placeholder
            : "Enter referral source (e.g., Google, Facebook, friend referral)"
        );
        setReferralUrlParamName(paramName !== null ? paramName : "code");

        // Debug logging to understand the issue
        console.log("🔍 Referral Field Debug Info:", {
          enabled: enabled !== null ? enabled : true,
          label: label !== null ? label : "How did you hear about us?",
          placeholder: placeholder !== null ? placeholder : "Enter referral source (e.g., Google, Facebook, friend referral)",
          paramName: paramName !== null ? paramName : "code",
          userAgent: navigator.userAgent,
          screenWidth: window.innerWidth,
          isMobile: window.innerWidth < 768
        });

        // Check for referral code in URL hash or search params
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(
          window.location.hash.substring(1)
        );
        const referralParam = paramName !== null ? paramName : "code";

        const referralCode =
          urlParams.get(referralParam) || hashParams.get(referralParam);

        if (referralCode) {
          setValue("referralSource", `Flyer Code: ${referralCode}`);
          console.log("✅ Auto-filled referral source from URL:", referralCode);

          // Track the referral code detection
          analytics.track("referral_code_detected", "contact_form", {
            code: referralCode,
            source: "url_parameter",
            timestamp: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.warn("⚠️ Failed to load referral settings:", error);
      }
    };

    loadReferralSettings();
  }, [setValue]);

  // Debug effect to track referral field state
  useEffect(() => {
    console.log("🎯 Referral field state changed:", {
      referralFieldEnabled,
      referralFieldLabel,
      screenWidth: window.innerWidth,
      isMobile: window.innerWidth < 768,
      userAgent: navigator.userAgent.includes('Mobile')
    });
  }, [referralFieldEnabled, referralFieldLabel]);

  // Save form data to session on changes (separate from customer data)
  const watchedFields = watch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Skip auto-save on initial load to prevent unnecessary saves
    if (
      Object.values(watchedFields).every(
        (value) => !value || value === "estimate"
      )
    ) {
      return;
    }

    // Debounce the updates to avoid excessive calls
    const timeoutId = setTimeout(() => {
      updateSessionData({
        formData: watchedFields,
        currentStep: "contact",
      });
    }, 500); // Increased debounce to 500ms for better UX

    return () => clearTimeout(timeoutId);
  }, [watchedFields]);

  const onSubmit = async (data: ContactFormData) => {
    // Honeypot check — if the hidden field has a value, silently block submission
    if (data.referralSource && data.referralSource.trim() !== '') {
      console.warn('🍯 Honeypot field filled — likely bot, silently ignoring');
      return;
    }

    console.log("🔍 Form submission attempted:", {
      computedIsAddressValidated,
      isAddressValidated,
      address: data.address,
      sessionValidation: getSessionData()?.addressValidation
    });
    
    // Validate address selection before proceeding
    // Check both state and session for maximum reliability
    const isValidFromState = isAddressValidated;
    const isValidFromSession = computedIsAddressValidated;
    // Also accept manual entry when the typed address is long enough (Google Places may be unavailable)
    const isValidFromManualEntry = (data.address?.trim().length ?? 0) >= 10;
    const finalValidation = isValidFromState || isValidFromSession || isValidFromManualEntry;
    
    if (!finalValidation) {
      console.log("❌ Address validation failed", {
        stateValidation: isValidFromState,
        sessionValidation: isValidFromSession,
        address: data.address,
        sessionData: getSessionData()?.addressValidation
      });
      // Show error message and prevent submission
      analytics.trackError("contact_form_validation_error", "Address not selected from dropdown", {
        address: data.address,
        timestamp: new Date().toISOString(),
      });
      setError("address", {
        type: "manual",
        message: "Please select your address from the suggestions, or include your suburb (e.g. \"12 Smith St, Melbourne VIC\").",
      });
      
      // Scroll to address field and focus it
      const addressInput = document.querySelector('#address-search') as HTMLInputElement;
      if (addressInput) {
        addressInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        addressInput.focus();
      }
      
      return; // Prevent form submission
    }

    // Even when the length-based manual-entry bypass above lets a typed
    // address through, a bare street address with no suburb/state/postcode
    // (e.g. "8 Carboni Court") silently produces an empty suburb once
    // parseSuburbFromAddress runs below — that incomplete address then flows
    // straight into SimPro with no way to schedule against it. Derive the
    // suburb up front and block submission if it can't be determined at all.
    const preSubmitSessionData = getSessionData();
    const preSubmitStructuredAddress = preSubmitSessionData?.customerData?.structuredAddress;
    const derivedSuburb = preSubmitStructuredAddress
      ? (preSubmitStructuredAddress.suburb || preSubmitStructuredAddress.city || '')
      : parseSuburbFromAddress(data.address);

    if (!derivedSuburb.trim()) {
      console.log("❌ Could not determine suburb from address", { address: data.address });
      analytics.trackError("contact_form_validation_error", "Suburb could not be determined from address", {
        address: data.address,
        timestamp: new Date().toISOString(),
      });
      setError("address", {
        type: "manual",
        message: "We couldn't find a suburb for that address — please select a suggestion or add your suburb (e.g. \"12 Smith St, Melbourne VIC\").",
      });

      const addressInput = document.querySelector('#address-search') as HTMLInputElement;
      if (addressInput) {
        addressInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        addressInput.focus();
      }

      return; // Prevent form submission
    }

    console.log("✅ Address validation passed, proceeding with submission");

    setLoading(true);
    analytics.trackFormStart("contact_form");

    try {
      // Create customer object
      const customer: Customer = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        address: data.address,
        // Extract suburb from structured address if available, fallback to parsing
        suburb: '',
        referralSource: data.referralSource,
      };

      // Save customer to database
      let savedCustomer;
      try {
        // Get session data before customer creation
        const sessionData = getSessionData();
        
        // DEBUG: Log session data to see if structuredAddress is present
        console.log('🔍 CONTACT FORM: Session data at form submission:', {
          'Has customerData?': !!sessionData?.customerData,
          'Has structuredAddress?': !!sessionData?.customerData?.structuredAddress,
          'structuredAddress': sessionData?.customerData?.structuredAddress,
          'address': sessionData?.customerData?.address,
          'Full sessionData.customerData': sessionData?.customerData
        });
        
        // Use structured address from Google Places if available
        if (sessionData?.customerData?.structuredAddress) {
          const structuredAddr = sessionData.customerData.structuredAddress;
          customer.suburb = structuredAddr.suburb || structuredAddr.city || '';
          // Add structured address to customer object for downstream use
          customer.structuredAddress = structuredAddr;
          console.log('🏠 Using Google Places structured address:', {
            suburb: customer.suburb,
            fullStructure: structuredAddr
          });
        } else {
          // Fallback to string parsing for backwards compatibility
          customer.suburb = parseSuburbFromAddress(data.address);
          console.log('📝 Fallback to address parsing, suburb:', customer.suburb);
        }

        // Pass session ID to link customer activities
        savedCustomer = await databaseService.upsertCustomer(
          customer,
          sessionData?.sessionId
        );

        // Create lead tracking
        if (sessionData) {
          try {
            const leadData = await databaseService.createLead({
              customerId: savedCustomer.id!,
              sessionId: sessionData.sessionId,
              utmSource: sessionData.utmData?.source || "",
              utmMedium: sessionData.utmData?.medium || "",
              utmCampaign: sessionData.utmData?.campaign || "",
              utmTerm: sessionData.utmData?.term || "",
              utmContent: sessionData.utmData?.content || "",
              stage: "contact",
              data: {
                contactFormData: data,
              },
            });

            // Save leadId to session for file organization
            if (leadData?.id) {
              updateSessionData({
                leadId: leadData.id,
              });
              console.log('✅ Lead created successfully:', leadData.id);
            }

            // Link UTM tracking to lead if UTM data exists
            if (utmData?.utmTrackingId && leadData?.id) {
              try {
                await fetch("/api/utm/link-lead", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    utmTrackingId: utmData.utmTrackingId,
                    leadId: leadData.id,
                  }),
                });
              } catch (linkError) {
                console.error("Error linking UTM to lead:", linkError);
              }
            }
          } catch (leadError) {
            // CRITICAL: Log lead creation failure with full context
            console.error('❌ LEAD CREATION FAILED - THIS IS A DATA LOSS EVENT', {
              error: leadError,
              customerId: savedCustomer.id,
              sessionId: sessionData.sessionId,
              customerEmail: customer.email,
              customerPhone: customer.phone,
              utmSource: sessionData.utmData?.source,
            });
            // Track this error for monitoring
            analytics.trackError('lead_creation_failed', `Customer ${savedCustomer.id}: ${leadError instanceof Error ? leadError.message : 'Unknown error'}`);
          }
        }

        customer.id = savedCustomer.id;
      } catch (dbError) {
        console.error("Database error:", dbError);
        // Continue with local data if database fails
      }

      // Enhanced GA4 lead tracking (non-PII) using gtag integration
      try {
        const sessionData = getSessionData();
        
        // Track form submission
        analytics.trackFormComplete('contact_form', {
          utm_source: sessionData?.utmData?.source,
          utm_medium: sessionData?.utmData?.medium,
          utm_campaign: sessionData?.utmData?.campaign,
          referral_source: data.referralSource ? 'provided' : 'not_provided'
        });

        // Track lead generation with non-PII data
        analytics.trackLeadGeneration('contact_form', 1);

        console.log("✅ GA4 form_complete and lead generation events sent via gtag");

      } catch (ga4Error) {
        console.error('GA4 tracking error:', ga4Error);
        // Don't block form submission if GA4 fails
      }

      // Save customer data
      setCustomer(customer);
      
      // Link customer to the logging system for real-time tracking
      if ((window as any).linkCustomer && savedCustomer?.id) {
        try {
          (window as any).linkCustomer({
            id: savedCustomer.id,
            email: customer.email,
            firstName: customer.firstName,
            lastName: customer.lastName,
            phone: customer.phone,
            address: customer.address
          });
          console.log('🔗 Customer linked to session for real-time tracking');
        } catch (linkError) {
          console.error('Failed to link customer to session:', linkError);
        }
      }

      // Update session - preserve existing customer data and merge with new data
      const existingSessionData = getSessionData();

      // CRITICAL: This is the start of a NEW booking flow. Clear any stale
      // bookingId / simproData left in localStorage from a PREVIOUS booking
      // by the same customer. Otherwise ConfirmationPage's duplicate-prevention
      // check short-circuits and shows a fake "Booking Confirmed" page with
      // the old SimPro reference (no DB row, no SimPro job, no SMS sent).
      // Symptom: tickets like Adrian Fitzgerald (Apr 2026 confirmation showing
      // job_id 500826 from Oct 2025) and Shane Fenton (new lead, no booking).
      clearStaleBookingState();

      updateSessionData({
        customerData: {
          ...existingSessionData?.customerData, // Preserve existing data (including structuredAddress, placeId)
          ...customer, // Add/override with new customer data
          // CRITICAL: Ensure structuredAddress is preserved if it exists
          structuredAddress: customer.structuredAddress || existingSessionData?.customerData?.structuredAddress,
          placeId: existingSessionData?.customerData?.placeId, // Preserve placeId
        },
        formData: data, // Keep form data intact
        currentStep: "details",
      });

      // Track completion
      analytics.trackFormComplete("contact_form", customer);

      // Get session data for UTM parameters
      const sessionData = getSessionData(); // Record journey step completion
      userJourneyService.recordStep("contact_form_completed", {
        customerId: customer.id,
        customerName: `${customer.firstName} ${customer.lastName}`,
        customerEmail: customer.email,
        utmSource: sessionData?.utmData?.source,
        utmMedium: sessionData?.utmData?.medium,
        utmCampaign: sessionData?.utmData?.campaign,
      }); // Trigger form completion webhook (once per session)
      // TODO: Enable webhooks when ready for production
      if (process.env.REACT_APP_ENABLE_WEBHOOKS === "true") {
        try {
          const webhookTriggered =
            await formWebhookService.triggerFormCompletion();
          if (webhookTriggered) {
            console.log("✅ Form completion webhook triggered successfully");
          }
        } catch (webhookError) {
          console.error("Form webhook error:", webhookError);
          // Don't block form submission if webhook fails
        }
      } else {
        console.log(
          "🚫 Webhooks disabled - set REACT_APP_ENABLE_WEBHOOKS=true to enable"
        );
      }

      // Navigate to next step
      setCurrentStep("measurement");
      analytics.trackNavigation("contact_form", "details");
      navigate("/details");
    } catch (error) {
      console.error("Error submitting contact form:", error);
      analytics.trackFormAbandonment("contact_form", "submit_error");

      // Record journey step error
      userJourneyService.recordStep("contact_form_error", {
        error: error instanceof Error ? error.message : "Unknown error",
        timestamp: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  };

  // Development helper to test form data persistence
  if (process.env.NODE_ENV === "development") {
    (window as any).testFormPersistence = () => {
      const sessionData = getSessionData();
      console.log("Current session data:", sessionData);
      console.log("Form data:", sessionData?.formData);
      console.log("Customer data:", sessionData?.customerData);
      console.log("Booking data:", sessionData?.bookingData);
    };
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Recovery loading state */}
      {recovering && (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-lg text-gray-600 dark:text-gray-300">Restoring your booking...</p>
        </div>
      )}

      {/* Podium welcome modal for ?pm visitors */}
      <PodiumWelcomeModal
        isOpen={showPodiumModal}
        onClose={() => setShowPodiumModal(false)}
      />

      {!recovering && <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border-2 border-primary/30 dark:border-gray-700 p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Online Self Booking
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Let's start with your contact details. We need these to help our friendly team prepare and help you when they call or visit.
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-8 overflow-x-auto">
          <div className="flex items-center space-x-2 sm:space-x-4 min-w-fit px-4 sm:px-0">
            <div className="flex flex-col items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">
                  1
                </div>
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-medium text-primary dark:text-primary-400 hidden xs:inline">
                  Contact
                </span>
              </div>
              {/* Mobile caption */}
              <div className="text-xs mt-1 text-primary dark:text-primary-400 xs:hidden">
                Contact
              </div>
            </div>
            <div className="w-4 sm:w-8 h-px bg-gray-300 dark:bg-gray-600" />
            <div className="flex flex-col items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400 rounded-full flex items-center justify-center text-sm font-medium">
                  2
                </div>
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden xs:inline">
                  Details
                </span>
              </div>
              {/* Mobile caption */}
              <div className="text-xs mt-1 text-gray-500 dark:text-gray-400 xs:hidden">
                Details
              </div>
            </div>
            <div className="w-4 sm:w-8 h-px bg-gray-300 dark:bg-gray-600" />
            <div className="flex flex-col items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400 rounded-full flex items-center justify-center text-sm font-medium">
                  3
                </div>
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden xs:inline">
                  Booking
                </span>
              </div>
              {/* Mobile caption */}
              <div className="text-xs mt-1 text-gray-500 dark:text-gray-400 xs:hidden">
                Booking
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit as any)} className="space-y-6">
          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              {...register("firstName")}
              label="First Name"
              placeholder="Enter your first name"
              error={errors.firstName?.message}
              icon={<User className="w-4 h-4" />}
              fullWidth
            />
            <Input
              {...register("lastName")}
              label="Last Name"
              placeholder="Enter your last name"
              error={errors.lastName?.message}
              icon={<User className="w-4 h-4" />}
              fullWidth
            />
          </div>

          {/* Email */}
          <Input
            {...register("email")}
            type="email"
            label="Email Address"
            placeholder="Enter your email address"
            error={errors.email?.message}
            icon={<Mail className="w-4 h-4" />}
            fullWidth
          />

          {/* Phone */}
          <Input
            {...register("phone")}
            type="tel"
            label="Phone Number"
            placeholder="Enter your phone number"
            error={errors.phone?.message}
            icon={<Phone className="w-4 h-4" />}
            fullWidth
          />

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Property Address{" "}
              <span className="text-red-500 text-lg font-bold">*</span>
            </label>{" "}
            <AddressSearch
              value={watch("address") || ""}
              onChange={async (address, placeId, coordinates, isValidated = false) => {
                setValue("address", address);
                setIsAddressValidated(isValidated); // Track validation state
                
                // Get existing session data to preserve it
                const existingSession = getSessionData();
                
                // Always save address validation state to session
                const sessionUpdate: any = {
                  customerData: {
                    ...existingSession?.customerData, // Preserve existing customer data
                    ...watchedFields, // Add current form fields
                    address, // Update address
                  },
                  currentStep: "contact",
                  // Save address validation state separately (backwards compatible)
                  addressValidation: {
                    isValidated,
                    placeId: placeId || undefined,
                    coordinates: coordinates || undefined,
                    timestamp: new Date().toISOString(),
                  },
                };

                if (placeId) {
                  // Get structured address components from Google Places
                  let structuredAddress = null;

                  try {
                    // Import Google Places service to extract structured address
                    const { googlePlacesService } = await import(
                      "../../services/googlePlacesService"
                    );
                    
                    console.log("🔍 DEBUG: Attempting to get place details for placeId:", placeId);
                    
                    // getPlaceDetails() handles initialization internally, no need to check isAvailable()
                    const placeDetails = await googlePlacesService.getPlaceDetails(placeId);
                    
                    console.log("🔍 DEBUG: getPlaceDetails returned:", placeDetails);
                    
                    if (placeDetails) {
                      structuredAddress = googlePlacesService.extractAddressComponents(placeDetails);
                      console.log("✅ Structured address from Google Places:", structuredAddress);
                    } else {
                      console.error("❌ getPlaceDetails returned null/undefined for placeId:", placeId);
                    }
                  } catch (error) {
                    console.error("❌ Failed to get structured address:", error);
                  }

                  // Add placeId and structured address to customerData
                  sessionUpdate.customerData.placeId = placeId;
                  sessionUpdate.customerData.structuredAddress = structuredAddress;

                  // Include coordinates if available (backwards compatibility)
                  if (coordinates) {
                    sessionUpdate.addressCoordinates = coordinates;
                    console.log(
                      "🗺️ Address coordinates captured:",
                      coordinates
                    );
                  }
                }

                // Wait for session to be saved to ensure validation state is persisted
                try {
                  await updateSessionData(sessionUpdate);
                  console.log("✅ Address validation saved to session:", {
                    address,
                    isValidated,
                    placeId: placeId || "none",
                    hasStructuredAddress: !!sessionUpdate.customerData?.structuredAddress,
                  });
                  
                  // Verify data was actually saved
                  const verifySession = getSessionData();
                  console.log("🔍 VERIFY: structuredAddress in session after save:", {
                    present: !!verifySession?.customerData?.structuredAddress,
                    data: verifySession?.customerData?.structuredAddress
                  });
                } catch (error) {
                  console.error("❌ Failed to save address validation to session:", error);
                }
              }}
              placeholder="Enter your property address"
              error={errors.address?.message}
              required
              requireValidation={true}
              initialValidated={computedIsAddressValidated}
              showValidationWarning={sessionLoaded && !computedIsAddressValidated && (watch("address")?.trim().length ?? 0) < 10}
            />

          </div>

          {/* Referral Source Field - Honeypot for bot detection */}
          {referralFieldEnabled && (
            <div className="absolute left-[-9999px] opacity-0 h-0 overflow-hidden" aria-hidden="true">
              <Input
                {...register("referralSource")}
                label={referralFieldLabel}
                placeholder={referralFieldPlaceholder}
                error={errors.referralSource?.message}
                fullWidth
                tabIndex={-1}
              />
            </div>
          )}

          {/* Submit Button */}
          <Button type="submit" loading={loading} fullWidth size="lg">
            Continue to Details
          </Button>
        </form>
      </div>}
    </div>
  );
};

export default ContactForm;
